import React, { useEffect, useState } from 'react';
import { getAnalytics, getExportStartups, getAnalyticsBySector, exportBySectorCsv } from '../../api';
import { useToast } from '../../components/Toast';

// Memes libelles et memes valeurs que la barre de filtres du suivi des
// startups, pour que les deux ecrans se lisent de la meme facon.
const STATUS_LABELS = { brouillon: 'Brouillon', en_attente: 'En attente', actif: 'Actif', suspendu: 'Suspendu', archive: 'Archivé' };
const DOSSIER_ACTION_LABELS = {
  nouveau: 'Nouveau', en_cours: 'En cours', valide: 'Validé',
  en_attente: 'En attente', rejete: 'Rejeté', publie: 'Publié',
};
const SECTEURS = ['Fintech', 'Insurtech', 'Regtech', 'Biotech', 'Greentech', 'Medtech', 'SaaS', 'HealthTech', 'Immotech'];
const EMPTY_FILTERS = { status: '', secteur: '', risk: '', trophy: '', action: '', submitted: '' };

export default function AdminDashboard() {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [sectorData, setSectorData] = useState(null);

  useEffect(() => { getAnalytics().then(setData).catch(console.error); }, []);

  // Les deux blocs sectoriels ont leur propre source filtree ; les autres blocs
  // du dashboard continuent de porter sur l'ensemble des startups.
  useEffect(() => {
    getAnalyticsBySector(filters).then(setSectorData).catch(console.error);
  }, [filters]);

  const setFilter = (key, value) => setFilters(f => ({ ...f, [key]: value }));
  const hasFilters = Object.values(filters).some(v => v !== '');
  const [sectorExporting, setSectorExporting] = useState(false);

  const handleExportSectors = async () => {
    setSectorExporting(true);
    try {
      const blob = await exportBySectorCsv(filters);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      // Date du jour dans le nom de fichier, comme cote serveur.
      a.href = url;
      a.download = `repartition_secteurs_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.message || "Échec de l'export");
    } finally {
      setSectorExporting(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const rows = await getExportStartups();
      // Generate CSV
      if (rows.length === 0) { toast.error('Aucune donnée'); setExporting(false); return; }
      const headers = Object.keys(rows[0]);
      const csv = [headers.join(';'), ...rows.map(r => headers.map(h => `"${(r[h] ?? '').toString().replace(/"/g, '""')}"`).join(';'))].join('\n');
      const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'export_startups_isi.csv'; a.click();
    } catch (err) { toast.error(err.message); }
    setExporting(false);
  };

  if (!data) return <div className="loading">Chargement des analytics...</div>;

  return (
    <div style={{ padding: '32px 0' }}>
      <div className="container">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h1>Dashboard Administrateur</h1>
          <button onClick={handleExport} className="btn btn-accent" disabled={exporting}>
            {exporting ? 'Export...' : 'Exporter CSV'}
          </button>
        </div>

        {/* KPIs */}
        <div className="stats-grid" style={{ marginBottom: 32 }}>
          <div className="stat-card"><div className="stat-value">{data.total}</div><div className="stat-label">Startups référencées</div></div>
          <div className="stat-card"><div className="stat-value" style={{ color: '#27AE60' }}>{data.trophy_participants}</div><div className="stat-label">Participants Trophées</div></div>
          <div className="stat-card"><div className="stat-value" style={{ color: '#E67E22' }}>{Math.max(0, Math.min(100, data.avg_qit_completion || 0)).toFixed(0)}%</div><div className="stat-label">Taux complétion QIT moyen</div></div>
          <div className="stat-card"><div className="stat-value" style={{ color: 'var(--primary)' }}>{data.user_activity.reduce((s,u) => s + parseInt(u.count), 0)}</div><div className="stat-label">Utilisateurs total</div></div>
        </div>

        {/* ===== Répartitions sectorielles (filtrables) ===== */}
        <div className="card" style={{ marginBottom: 16, padding: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>
            Filtres — appliqués aux deux répartitions par secteur ci-dessous
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <select value={filters.status} onChange={e => setFilter('status', e.target.value)} className="form-input" style={{ maxWidth: 180, fontSize: 13 }}>
              <option value="">Tous les statuts</option>
              {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <select value={filters.secteur} onChange={e => setFilter('secteur', e.target.value)} className="form-input" style={{ maxWidth: 160, fontSize: 13 }}>
              <option value="">Tous les secteurs</option>
              {SECTEURS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={filters.risk} onChange={e => setFilter('risk', e.target.value)} className="form-input" style={{ maxWidth: 160, fontSize: 13 }}>
              <option value="">Tous les risques</option>
              <option value="vert">Vert (Résilient)</option>
              <option value="orange">Orange (Vigilance)</option>
              <option value="rouge">Rouge (Critique)</option>
            </select>
            <select value={filters.trophy} onChange={e => setFilter('trophy', e.target.value)} className="form-input" style={{ maxWidth: 160, fontSize: 13 }}>
              <option value="">Trophées : tous</option>
              <option value="oui">Participants</option>
              <option value="non">Non participants</option>
            </select>
            <select value={filters.action} onChange={e => setFilter('action', e.target.value)} className="form-input" style={{ maxWidth: 200, fontSize: 13 }}>
              <option value="">Actions : toutes</option>
              <option value="aucune">Aucune action</option>
              {Object.entries(DOSSIER_ACTION_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <select value={filters.submitted} onChange={e => setFilter('submitted', e.target.value)} className="form-input" style={{ maxWidth: 180, fontSize: 13 }}>
              <option value="">Candidature : toutes</option>
              <option value="soumis">Soumises</option>
              <option value="non_soumis">Non soumises</option>
            </select>
            {hasFilters && (
              <button onClick={() => setFilters(EMPTY_FILTERS)} className="btn btn-outline" style={{ fontSize: 12, padding: '6px 14px' }}>
                Réinitialiser
              </button>
            )}
            <span style={{ fontSize: 12, color: 'var(--text-light)', marginLeft: 'auto' }}>
              {sectorData ? `${sectorData.total} startup${sectorData.total > 1 ? 's' : ''}` : '...'}
              {hasFilters && data ? ` sur ${data.total}` : ''}
            </span>
            <button onClick={handleExportSectors} className="btn btn-accent"
              disabled={sectorExporting || !sectorData || sectorData.total === 0}
              style={{ fontSize: 12, padding: '6px 14px',
                opacity: (sectorExporting || !sectorData || sectorData.total === 0) ? 0.6 : 1 }}>
              {sectorExporting ? 'Export…' : 'Exporter les secteurs (CSV)'}
            </button>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 24 }}>
          {/* By sector */}
          <div className="card">
            <h3 style={{ marginBottom: 16 }}>Par secteur</h3>
            {!sectorData ? <div className="loading">Chargement...</div>
              : sectorData.by_sector.length === 0 ? (
                <p style={{ fontSize: 13, color: 'var(--text-light)', margin: 0 }}>
                  Aucune startup ne correspond à ces filtres.
                </p>
              ) : sectorData.by_sector.map(s => {
                const pct = sectorData.total ? (parseInt(s.count) / sectorData.total * 100) : 0;
                return (
                  <div key={s.categorie} style={{ marginBottom: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                      <span>{s.categorie}</span><span>{s.count} ({pct.toFixed(0)}%)</span>
                    </div>
                    <div style={{ height: 8, background: '#F0F0F0', borderRadius: 4 }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: 'var(--primary-light)', borderRadius: 4 }} />
                    </div>
                  </div>
                );
              })}
          </div>

          {/* By sector — SaaS réaffecté (vue dérivée) */}
          <div className="card">
            <h3 style={{ marginBottom: 4 }}>Par secteur (SaaS réaffecté)</h3>
            <div style={{ fontSize: 12, color: 'var(--text-light)', marginBottom: 16 }}>
              Vue dérivée : les startups « SaaS » sont recomptées dans leur secteur requalifié — la catégorie en base n'est pas modifiée. Détail et export dans Outils → Re-ventilation SaaS.
            </div>
            {!sectorData ? <div className="loading">Chargement...</div>
              : sectorData.by_sector_reallocated.length === 0 ? (
                <p style={{ fontSize: 13, color: 'var(--text-light)', margin: 0 }}>
                  Aucune startup ne correspond à ces filtres.
                </p>
              ) : sectorData.by_sector_reallocated.map(s => {
                const pct = sectorData.total ? (parseInt(s.count) / sectorData.total * 100) : 0;
                return (
                  <div key={s.categorie} style={{ marginBottom: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                      <span>{s.categorie}</span><span>{s.count} ({pct.toFixed(0)}%)</span>
                    </div>
                    <div style={{ height: 8, background: '#F0F0F0', borderRadius: 4 }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: '#27AE60', borderRadius: 4 }} />
                    </div>
                  </div>
                );
              })}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 24 }}>
          {/* By status */}
          <div className="card">
            <h3 style={{ marginBottom: 16 }}>Par statut</h3>
            {data.by_status.map(s => (
              <div key={s.status} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                <span style={{ textTransform: 'capitalize' }}>{s.status}</span>
                <strong>{s.count}</strong>
              </div>
            ))}
          </div>

          {/* Score distribution */}
          <div className="card">
            <h3 style={{ marginBottom: 16 }}>Distribution des scores ISI</h3>
            {data.score_distribution.map(s => {
              const cnt = parseInt(s.count);
              const pct = (cnt / data.total * 100);
              const color = s.tranche.includes('Excellent') ? '#27AE60' : s.tranche.includes('Bon') ? '#2ECC71' : s.tranche.includes('Moyen') ? '#F39C12' : s.tranche.includes('Faible') ? '#E67E22' : '#E74C3C';
              return (
                <div key={s.tranche} style={{ marginBottom: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                    <span>{s.tranche}</span><span>{cnt}</span>
                  </div>
                  <div style={{ height: 8, background: '#F0F0F0', borderRadius: 4 }}>
                    <div style={{ width: `${Math.max(pct, 2)}%`, height: '100%', background: color, borderRadius: 4 }} />
                  </div>
                </div>
              );
            })}
          </div>

          {/* By region */}
          <div className="card">
            <h3 style={{ marginBottom: 16 }}>Par région (top 10)</h3>
            {data.by_region.map(r => (
              <div key={r.region} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                <span>{r.region}</span><strong>{r.count}</strong>
              </div>
            ))}
          </div>

          {/* Users by role */}
          <div className="card">
            <h3 style={{ marginBottom: 16 }}>Utilisateurs par rôle</h3>
            {data.user_activity.map(u => (
              <div key={u.role} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                <span style={{ textTransform: 'capitalize' }}>{u.role}</span><strong>{u.count}</strong>
              </div>
            ))}
          </div>

          {/* By stade */}
          <div className="card">
            <h3 style={{ marginBottom: 16 }}>Par stade de développement</h3>
            {data.by_stade.map(s => (
              <div key={s.stade_dev} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                <span>{s.stade_dev}</span><strong>{s.count}</strong>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
